import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { styled } from '@mui/material';
import MuiButton from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import LogoutIcon from '@mui/icons-material/Logout';

import { RootState, useAppSelector } from '@/state/store';
import { authentication } from '@/actions';
import { Avatar, Container, Link } from '@/components';
import Notifications from './Notifications';
import { createSelector } from '@reduxjs/toolkit';
import BasicLayout, { LayoutProps as BasicLayoutProps } from './BasicLayout';
import { AuthenticationStatus } from '@/contracts';
import hinted from '@/components/Hinted';
import t from '@/utils/translate';
import BackgroundProcesses from './BackgroundProcesses';
import BackgroundContext from '@/components/background/BackgroundContext';

const AvatarsBlock = styled('div')({
  display: 'flex',
  gap: '8px',
});

const UserMenuAnchor = styled(Avatar)`
  cursor: pointer;
  width: 32px;
  height: 32px;
  font-size: 1.2rem;
  line-height: normal;
`;

const SuperAvatar = hinted(
  styled(Avatar)(({ color }) => ({
    cursor: 'pointer',
    width: '32px',
    height: '32px',
    fontSize: '14px',
    fontWeight: '500',
    backgroundColor: color,
  })),
  { placement: 'bottom-end', delay: 250, fadeDuration: 50 }
);

const UserMenu = styled(Container)(({ theme }) => ({
  width: theme.spacing(50),
  padding: theme.spacing(3)
}));

const CustomButton = styled(MuiButton)`
  justify-content: left;
`;

const RightTools = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: right;
  gap: 20px;
`;

type LayoutProps = BasicLayoutProps & { header?: ReactNode };

export default ({ header, ...props }: LayoutProps) => {
  
  const background = useContext(BackgroundContext);

  const { authenticated, profile, avatars } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.authentication.status === AuthenticationStatus.AUTHENTICATED,
        (state: RootState) => state.authentication.profile,
        (state: RootState) => state.user.avatars
      ],
      (authenticated, profile, avatars) => ({ authenticated, profile, avatars })
    )
  );

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showBackgroundProcesses, setShowBackgroundProcesses ] = useState(false);

  useEffect(() => {
    const onPageExitBlock = () => setShowBackgroundProcesses(true);
    background.addEventListener('block', onPageExitBlock);
    return () => background.removeEventListener('block', onPageExitBlock);
  }, [ background ]);

  return <>
    <BasicLayout
      header-center={header}
      {...props}
      header-right-side={
        <RightTools>
          {props['header-right-side']}
          
          {authenticated && <Notifications />}

          <AvatarsBlock>
            {profile?.isSuperadmin
              ? (<Link to={'/admin'}><SuperAvatar color='#DE350B' profile='SA' hint={t('user.hint.sa')}/></Link>)
              : (profile?.isSupervisor
                && <SuperAvatar color='#6554C0' profile='SV' hint={t('user.hint.sv')}/>
              )
            }
            <UserMenuAnchor
              source={profile && avatars[profile.id]?.source}
              profile={profile}
              onClick={e => setAnchorEl(e.currentTarget)}
            />
          </AvatarsBlock>

          <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {authenticated
              ? (<UserMenu>
                <Link to='/profile' exitPoint>
                  <CustomButton
                    color='inherit'
                    fullWidth
                  >
                    {t('navigation.profile')}
                  </CustomButton>
                </Link>
                <Link to='/project'>
                  <CustomButton
                    color='inherit'
                    fullWidth
                  >
                    {t('navigation.projects')}
                  </CustomButton>
                </Link>
                <CustomButton
                  color='inherit'
                  fullWidth
                  onClick={() => authentication.logout()}
                  startIcon={<LogoutIcon />}
                >
                  {t('navigation.exit')}
                </CustomButton>
              </UserMenu>)
              : (<UserMenu>
                <Link to='/registration' exitPoint>
                  <CustomButton
                    color='inherit'
                    fullWidth
                  >
                    {t('navigation.registration')}
                  </CustomButton>
                </Link>
                <Link to='/login' exitPoint>
                  <CustomButton
                    color='inherit'
                    fullWidth
                  >
                    {t('navigation.login')}
                  </CustomButton>
                </Link>
              </UserMenu>)
            }
          </Popover>
        </RightTools>
      }
    />
    {authenticated && showBackgroundProcesses && (
      <BackgroundProcesses onClose={() => setShowBackgroundProcesses(false)}/>
    )}
  </>;
};
